import EmailMessageModel from '@/common/models/email-message-model';

export default {
    provide() {
        return {
            // used by modals
            reactivate: this.reactivate,
            cancel: this.cancel,
            sendEmailToCustomer: this.sendEmailToCustomer,
            setRenewalTime: this.setRenewalTime,
        };
    },
    methods: {
        /**
         * Provided injection to a modal down the component chain that reactivates the
         * order and optionally sends an email to inform the customer.
         * @param {Object} data
         * @param {boolean} sendEmail
         * @return {Promise<void>}
         */
        async reactivate({data, sendEmail}) {
            await this.toggleOrder({
                sendEmail,
                delegate: () => this.$api(
                    ({subscriptionReactivations}) => subscriptionReactivations.reactivate({data}),
                ),
                emailSubject: `Your order (#${this.order.id}) has been reactivated`,
                emailText: `Dear ${this.customer.displayName},\n\nYour order (#${this.order.id}) has been reactivated.\n\nThank you`,
                successMsg: 'Order has been reactivated successfully.',
                errorMsg: 'Unable to reactivate this order.',
            });
        },
        /**
         * Provided injection to a modal down the component chain that cancels the
         * order and optionally sends an email to inform the customer.
         * @param {Object} data
         * @param {boolean} sendEmail
         * @return {Promise<void>}
         */
        async cancel({data, sendEmail}) {
            await this.toggleOrder({
                sendEmail,
                delegate: () => this.$api(
                    ({subscriptionCancellations}) => subscriptionCancellations.create({data}),
                ),
                emailSubject: `Your order (#${this.order.id}) has been canceled`,
                emailText: `Dear ${this.customer.displayName},\n\nYour order (#${this.order.id}) has been canceled.\n\nThank you`,
                successMsg: 'Order has been canceled successfully.',
                errorMsg: 'Unable to cancel this order.',
            });
        },
        /**
         * Update order RenewalTime
         * @param {string} renewalTime (utc)
         * @returns {Promise<void>}
         */
        async setRenewalTime(renewalTime) {
            try {
                await this.updateOrder({
                    renewalTime,
                });
                this.$toast.positive(`Renewal Time successfully updated.`);
            } catch (e) {
                this.$toast.fromError(e, `Unable to update Renewal Time, please contact support`);
            }
        },
        /**
         * Send an email to the customer to inform them of an action the merchant has taken
         * with this order.
         * @param {string} subject
         * @param {string} text
         */
        sendEmailToCustomer({subject, text}) {
            const email = new EmailMessageModel({
                to: [this.customer.email],
                from: this.$store.getters['user/email'],
                subject,
                text,
                metadata: {
                    customerId: this.customer.id,
                    eventType: 'direct-email-sent',
                },
                attachments: [],
            });
            (async () => {
                const data = email.getPayload();
                try {
                    const message = await this.$api(
                        ({emailMessages}) => emailMessages.create({data}),
                    );
                    await this.$api(
                        ({emailMessages}) => emailMessages.send({id: message.fields.id}),
                    );
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to send the email. Please try again later or contact support.');
                }
            })();
        },
        /**
         * Toggles the order based on the delegate (cancellation or reactivation) and
         * handles it in a generic way by sending an email if required and displaying a
         * confirmation or error message once completed.
         * @param {Function} delegate
         * @param {boolean} sendEmail
         * @param {string} emailSubject
         * @param {string} emailText
         * @param {string} successMsg
         * @param {string} errorMsg
         * @return {Promise<void>}
         */
        async toggleOrder(
            {
                delegate,
                sendEmail,
                emailSubject,
                emailText,
                successMsg,
                errorMsg,
            },
        ) {
            try {
                // this.startLoadingPage();
                const emailCustomer = () => {
                    if (sendEmail) {
                        this.sendEmailToCustomer({
                            subject: emailSubject,
                            text: emailText,
                        });
                    }
                };
                await delegate();
                await Promise.all([
                    this.refresh(),
                    emailCustomer(),
                ]);
                this.$toast.positive(successMsg);
            } catch (error) {
                // this.stopLoadingPage();
                this.$toast.negative(errorMsg);
            }
        },
        async updateOrder(data) {
            this.isUpdatingOrder = true;
            await this.$api(({subscriptions}) => subscriptions.update({
                id: this.order.id,
                data: {
                    ...this.order.getPayload(),
                    ...data,
                },
            }));
            this.refresh();
            this.isUpdatingOrder = false;
        },
        /**
         * Refresh the local data to update all UI elements after a modification to the order.
         * or a reactivation.
         * @return {Promise<void>}
         */
        async refresh() {
            this.isDataReady = false;
            this.$emit('refresh');
            if (this.orderSummaryRefreshOrder) {
                this.orderSummaryRefreshOrder();
            }
        },
    },
};
